/**
 * Modernleştirilmiş Sinematik Giriş Ekranı.
 *
 * Özellikler:
 * - Cam morfolojisi (glassmorphism) ve derin sinematik arka plan aurası
 * - Parola gizle/göster anahtarı
 * - 2 Faktörlü Doğrulama (TOTP 6 haneli kod) geçişi ve otomatik gönderme
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandMark } from '../components/BrandMark.tsx';
import {
  AlertIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  ShieldIcon,
  UserIcon,
} from '../Icon.tsx';
import { shouldAutoSubmitTwoFactor } from '../login2fa.ts';
import { useSession } from '../session.tsx';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: giriş ekranı 2FA ve çevrimdışı devam durumlarını tek yerde yönetir
export function LoginScreen(): ReactNode {
  const { login, error, offlineUser, continueOffline } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [is2faStep, setIs2faStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const twoFactorInputRef = useRef<HTMLInputElement>(null);
  const lastAttemptedCodeRef = useRef<string | null>(null);

  const handleLogin = useCallback(
    async (codeToVerify?: string) => {
      if (codeToVerify !== undefined) {
        lastAttemptedCodeRef.current = codeToVerify.trim();
      }
      setBusy(true);
      setFailure(null);
      try {
        const res = await login(username, password, codeToVerify);
        if (res?.require2fa) {
          setIs2faStep(true);
          if (codeToVerify) {
            setFailure('Girdiğiniz 6 haneli kod hatalı veya süresi dolmuş.');
            // Aynı kod tekrar otomatik gönderilmesin; kullanıcı yeni kodu girsin.
            setTwoFactorCode('');
            lastAttemptedCodeRef.current = null;
            requestAnimationFrame(() => twoFactorInputRef.current?.focus());
          } else {
            setFailure(null);
          }
        }
      } catch (caught) {
        setFailure(caught instanceof Error ? caught.message : 'Giriş başarısız.');
      } finally {
        setBusy(false);
      }
    },
    [login, username, password],
  );

  useEffect(() => {
    if (is2faStep) {
      twoFactorInputRef.current?.focus();
    }
  }, [is2faStep]);

  // 6 haneli kod yazıldığında **yalnız bir kez** otomatik doğrulama.
  useEffect(() => {
    if (
      !shouldAutoSubmitTwoFactor({
        isTwoFactorStep: is2faStep,
        code: twoFactorCode,
        busy,
        lastAttemptedCode: lastAttemptedCodeRef.current,
      })
    ) {
      return;
    }
    void handleLogin(twoFactorCode.trim());
  }, [twoFactorCode, is2faStep, busy, handleLogin]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (is2faStep) {
      await handleLogin(twoFactorCode.trim());
    } else {
      await handleLogin();
    }
  };

  const backToCredentials = () => {
    setIs2faStep(false);
    setTwoFactorCode('');
    setFailure(null);
    lastAttemptedCodeRef.current = null;
  };

  return (
    <main className="login">
      {/* Sinematik arka plan aurası */}
      <div className="login__ambient" aria-hidden="true">
        <div className="login__glow login__glow--1" />
        <div className="login__glow login__glow--2" />
      </div>

      <div className="login__wrapper">
        <form className="login__card" onSubmit={submit}>
          <div className="login__header">
            <div className="login__logo-badge">
              <BrandMark size={48} />
            </div>
            <h1 className="login__brand">pbfork</h1>
            <p className="login__hint">Kişisel medya sunucusu</p>
          </div>

          {offlineUser !== null && !is2faStep && (
            <div className="login__offline" role="status">
              <p className="login__offline-text">
                İnternet bağlantısı yok. Bu cihazda <strong>{offlineUser.displayName}</strong>{' '}
                hesabının çevrimdışı kataloğu var.
              </p>
              <button
                type="button"
                className="button button--ghost login__offline-btn"
                onClick={continueOffline}
              >
                Çevrimdışı devam et
              </button>
              <p className="login__offline-note">
                Yalnızca kayıtlı katalog görüntülenir; oynatma için bağlantı gerekir.
              </p>
            </div>
          )}

          {!is2faStep ? (
            <>
              <label className="field">
                <span className="field__label">
                  <span className="field__icon">
                    <UserIcon size={15} />
                  </span>
                  Kullanıcı Adı
                </span>
                <input
                  className="field__input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  placeholder="Kullanıcı adınızı girin"
                  required
                />
              </label>

              <label className="field">
                <span className="field__label">
                  <span className="field__icon">
                    <LockIcon size={15} />
                  </span>
                  Parola
                </span>
                <div className="field__input-wrapper">
                  <input
                    className="field__input field__input--with-btn"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Parolanızı girin"
                    required
                  />
                  <button
                    type="button"
                    className="field__toggle-btn"
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? 'Parolayı Gizle' : 'Parolayı Göster'}
                    aria-label={showPassword ? 'Parolayı Gizle' : 'Parolayı Göster'}
                  >
                    {showPassword ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
                  </button>
                </div>
              </label>

              {(failure || error) && (
                <div className="login__error-box" role="alert">
                  <span className="login__error-icon">
                    <AlertIcon size={16} />
                  </span>
                  <p className="login__error-text">{failure ?? error}</p>
                </div>
              )}

              <button
                type="submit"
                className="button button--primary login__submit-btn"
                disabled={busy}
              >
                {busy ? (
                  <span className="login__btn-content">
                    <span className="spinner spinner--small" /> Giriş Yapılıyor…
                  </span>
                ) : (
                  <>
                    <span>Giriş Yap</span>
                    <ArrowRightIcon size={16} />
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="login__2fa-step">
              <div className="login__2fa-badge">
                <span className="login__2fa-icon">
                  <ShieldIcon size={22} />
                </span>
                <span className="login__2fa-title">İki Faktörlü Doğrulama</span>
              </div>

              <p className="login__2fa-desc">
                Google Authenticator veya kimlik doğrulayıcı uygulamanızdaki{' '}
                <strong>6 haneli geçici kodu</strong> girin:
              </p>

              <label className="field">
                <input
                  ref={twoFactorInputRef}
                  className="field__input login__2fa-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••••"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  required
                />
              </label>

              {(failure || error) && (
                <div className="login__error-box" role="alert">
                  <span className="login__error-icon">
                    <AlertIcon size={16} />
                  </span>
                  <p className="login__error-text">{failure ?? error}</p>
                </div>
              )}

              <button
                type="submit"
                className="button button--primary login__submit-btn"
                disabled={busy || twoFactorCode.length < 6}
              >
                {busy ? (
                  <span className="login__btn-content">
                    <span className="spinner spinner--small" /> Doğrulanıyor…
                  </span>
                ) : (
                  'Kodu Doğrula ve Gir'
                )}
              </button>

              <button
                type="button"
                className="button button--ghost login__back-btn"
                onClick={backToCredentials}
              >
                <ArrowLeftIcon size={16} />
                Farklı kullanıcı ile giriş yap
              </button>
            </div>
          )}
        </form>
      </div>
    </main>
  );
}
